import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { grantCredits, setCreditsExact, updateUserAdmin } from '../lib/credits';
import { storageImage } from '../lib/img';
import { adminCreateUser, adminUpdateUserAuth, adminDeleteUser } from '../lib/adminUsers';
import AdminSupportPanel from './AdminSupportPanel';
import AdminTeamPanel from './AdminTeamPanel';
import AdminReportsPanel from './AdminReportsPanel';
import DrewsPickTab from '../components/admin/DrewsPickTab';
import FeaturedTab from '../components/admin/FeaturedTab';
import AuditTab from '../components/admin/AuditTab';
import SponsorsTab from '../components/admin/SponsorsTab';
import BrandsTab from '../components/admin/BrandsTab';
import { useAuth } from '../components/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from '../components/workspace/ConfirmModal';
import Spinner from '../components/shared/Spinner';

// ─── In-app prompt modal ─────────────────────────────────────────────────
// Replaces window.prompt for admin flows. iOS Safari (PWA mode) and some
// popup-blocker configs silently suppress native prompts, which would
// turn admin actions into no-ops without explanation.
function AdminPromptModal({ open, title, body, defaultValue = '', placeholder, confirmLabel = 'OK', busy = false, onCancel, onConfirm }) {
  const [val, setVal] = useState(defaultValue);
  useEffect(() => { if (open) setVal(defaultValue); }, [open, defaultValue]);
  if (!open) return null;
  function submit(e) { e?.preventDefault?.(); onConfirm?.(val); }
  return (
    <div
      className="ws-modal-wrap ws-confirm-wrap"
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel?.()}
      role="dialog"
      aria-modal="true"
    >
      <form className="ws-modal ws-confirm-modal" onSubmit={submit}>
        <div className="ws-confirm-head">
          <div className="ws-confirm-titles">
            <h2 className="ws-confirm-title">{title}</h2>
            {body && <p className="ws-confirm-body">{body}</p>}
          </div>
        </div>
        <div style={{ padding: '0 20px 4px' }}>
          <input
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder={placeholder}
            autoFocus
            className="ws-modal-input"
            style={{ width: '100%' }}
          />
        </div>
        <div className="ws-confirm-actions">
          <button type="button" className="ws-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="ws-btn-confirm ws-btn-confirm-primary" disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Legacy cost estimates (kept for the old overview tiles that predate
// real logging; the Costs tab now uses real claude_usage data). ────────────
const COST_PER_EPISODE = 0.08;
const COST_PER_IDEA_REFRESH = 0.02;

// ── Fetch helpers ────────────────────────────────────────────────────────────
async function fetchAdminData() {
  if (!supabase) return null;

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const last7d  = new Date(Date.now() - 7  * 86400000).toISOString();
  const last30d = new Date(Date.now() - 30 * 86400000).toISOString();

  // Run queries in parallel
  const [usersRes, episodesRes, todayEpsRes, weekEpsRes, recentUsersRes,
         usage30dRes, usageRecentRes] = await Promise.all([
    supabase.from('podcaster_profiles').select('user_id, email, full_name, plan, niche, episodes_created, created_at, verified', { count: 'exact' }),
    supabase.from('episodes').select('id, user_id, topic, format, created_at', { count: 'exact' }).gte('created_at', startOfMonth),
    supabase.from('episodes').select('id, user_id, topic, format, created_at').gte('created_at', startOfToday).order('created_at', { ascending: false }),
    supabase.from('episodes').select('id, created_at').gte('created_at', last7d),
    supabase.from('podcaster_profiles').select('user_id, email, full_name, plan, niche, episodes_created, created_at, verified').order('created_at', { ascending: false }).limit(20),
    // Last 30d of Claude calls powers today / 7d / 30d spend + breakdowns.
    // The table is append-only, indexed on created_at — fine to pull flat.
    supabase.from('claude_usage')
      .select('id, created_at, user_id, endpoint, model, input_tokens, output_tokens, cache_read_input_tokens, cache_creation_input_tokens, estimated_cost_usd, duration_ms, error')
      .gte('created_at', last30d)
      .order('created_at', { ascending: false })
      .limit(5000),
    // Just the last 50 for the "recent calls" tail.
    supabase.from('claude_usage')
      .select('id, created_at, user_id, endpoint, model, input_tokens, output_tokens, estimated_cost_usd, duration_ms, error')
      .order('created_at', { ascending: false })
      .limit(50),
  ]);

  // ── All-time spend (mini-spec #60). Pulled separately so we can
  // sum without dragging every row into memory client-side. ─────────
  const { data: allTimeSumRows } = await supabase
    .from('claude_usage')
    .select('estimated_cost_usd')
    .limit(50000);
  const allTimeSpend = (allTimeSumRows || []).reduce(
    (acc, r) => acc + (Number(r.estimated_cost_usd) || 0),
    0
  );

  return {
    users: recentUsersRes.data || [],
    totalUsers: usersRes.count || 0,
    monthlyEpisodes: episodesRes.count || 0,
    todayEpisodes: todayEpsRes.data || [],
    weekEpisodes: weekEpsRes.data || [],
    allUsers: usersRes.data || [],
    // Null (not empty array) tells the UI "the claude_usage table is missing
    // or the admin-read RLS hasn't been applied yet" so we can prompt the
    // user to run the migration.
    usage30d:     usage30dRes.error     ? null : (usage30dRes.data     || []),
    usageRecent:  usageRecentRes.error  ? null : (usageRecentRes.data  || []),
    usageError:   usage30dRes.error?.message || usageRecentRes.error?.message || null,
    allTimeSpend, // mini-spec #60
  };
}

// ── Aggregate a usage[] window into a shape the UI renders ──────────────────
// ── Tabs ─────────────────────────────────────────────────────────────────
// Tab visibility is scope-driven. Admins see every tab. Managers see
// only the tabs their scopes unlock:
//   • 'users'    → users + verifications + activity (Activity is read-
//                  only audit log, fits the users scope)
//   • 'support'  → support
//   • 'content'  → drew's pick (curation surface; future "shelves" /
//                  "banners" tabs land in this scope)
// Overview + costs + team are admin-only (financial / governance).
function AdminPageTabs({ activeTab, onChange }) {
  const { isAdmin, hasScope } = useAuth();

  const tabs = [
    { id: 'overview',      label: 'Overview',      show: isAdmin },
    { id: 'costs',         label: 'Costs',         show: isAdmin },
    { id: 'users',         label: 'Users',         show: isAdmin || hasScope('users') },
    { id: 'verifications', label: 'Verifications', show: isAdmin || hasScope('users') },
    { id: 'activity',      label: 'Activity',      show: isAdmin || hasScope('users') },
    { id: 'support',       label: 'Support',       show: isAdmin || hasScope('support') },
    { id: 'reports',       label: 'Reports',       show: isAdmin || hasScope('moderation') },
    { id: "drew's pick",   label: "Drew's pick",   show: isAdmin || hasScope('content') },
    { id: 'featured',      label: 'Featured',      show: isAdmin || hasScope('content') },
    { id: 'sponsors',      label: 'Sponsors',      show: isAdmin || hasScope('content') },
    { id: 'brands',        label: 'Brands',        show: isAdmin || hasScope('content') },
    { id: 'audit',         label: 'Audit',         show: isAdmin },
    { id: 'team',          label: 'Team',          show: isAdmin },
  ].filter(t => t.show);

  return (
    <div className="mkt-tabs" style={{ marginBottom: 28 }}>
      {tabs.map(t => (
        <button key={t.id} className={`mkt-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// Pick the first tab this user can see — used to seed the initial
// active tab when a non-admin opens /admin. Returns 'overview' for
// admins (their natural landing) and falls through the scope list for
// managers. Keep in sync with AdminPageTabs above.
function firstAllowedTab({ isAdmin, hasScope }) {
  if (isAdmin) return 'overview';
  if (hasScope('support'))    return 'support';
  if (hasScope('moderation')) return 'reports';
  if (hasScope('users'))      return 'users';
  if (hasScope('content'))    return "drew's pick";
  return 'overview';   // shouldn't render — route gate above catches no-access
}

function aggregateUsage(rows, sinceMs) {
  const since = sinceMs ? Date.now() - sinceMs : 0;
  const subset = sinceMs ? rows.filter(r => new Date(r.created_at).getTime() >= since) : rows;
  let cost = 0, calls = 0, inTok = 0, outTok = 0, errors = 0;
  const byModel = {};
  const byEndpoint = {};
  const byUser = {};
  for (const r of subset) {
    cost += Number(r.estimated_cost_usd) || 0;
    calls += 1;
    inTok  += r.input_tokens  || 0;
    outTok += r.output_tokens || 0;
    if (r.error) errors += 1;
    byModel[r.model]       = (byModel[r.model]       || 0) + (Number(r.estimated_cost_usd) || 0);
    byEndpoint[r.endpoint] = (byEndpoint[r.endpoint] || 0) + (Number(r.estimated_cost_usd) || 0);
    const key = r.user_id || '(unauthenticated)';
    if (!byUser[key]) byUser[key] = { cost: 0, calls: 0 };
    byUser[key].cost  += Number(r.estimated_cost_usd) || 0;
    byUser[key].calls += 1;
  }
  return { cost, calls, inTok, outTok, errors, byModel, byEndpoint, byUser };
}

// ── Small components ─────────────────────────────────────────────────────────

function CostCard({ label, value, sub, trend, trendDir }) {
  return (
    <div className="admin-cost-card">
      <div className="admin-cost-label">{label}</div>
      <div className="admin-cost-value">{value}</div>
      {sub && <div className="admin-cost-sub">{sub}</div>}
      {trend && <div className={`admin-cost-trend ${trendDir || 'neutral'}`}>{trend}</div>}
    </div>
  );
}

const PLAN_COLORS = { Free: 'var(--muted)', Creator: '#ffa804', Pro: '#6C7FFF' };
const STATUS_COLORS = { active: '#4ade80', flagged: '#FF5555', churned: 'var(--muted)' };

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
  if (m < 2) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

// ── Main Admin Page ──────────────────────────────────────────────────────────

export default function AdminPage() {
  const { isAdmin, isManager, hasAnyCmsAccess, hasScope, profile, user } = useAuth();
  const adminFirstName = ((profile?.full_name || user?.email || '').split(/[ @]/)[0] || '').trim();
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Default tab is the first one this user can see. Admin lands on
  // 'overview', a manager scoped to 'support' lands on 'support', etc.
  const [activeTab, setActiveTab] = useState(() => firstAllowedTab({ isAdmin, hasScope }));
  const [openUser, setOpenUser] = useState(null); // mini-spec #60
  const [showNewUser, setShowNewUser] = useState(false);

  useEffect(() => {
    // Only fetch the admin-data feed when this user can see the
    // overview/costs/users surfaces — managers without those scopes
    // don't need the data and the queries can be expensive.
    if (isAdmin) {
      fetchAdminData().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  // Route gate — if a signed-in user lands on /admin without any
  // CMS access (no admin row, no manager scopes), bounce them home.
  // Loading state shows a spinner while AuthContext resolves their
  // role; once resolved, gate fires.
  if (!hasAnyCmsAccess) {
    return (
      <div className="admin-shell" style={{ padding: '48px 24px', textAlign: 'center', color: 'rgba(245,237,224,0.65)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f5ede0', marginBottom: 8 }}>
          You don't have access to the admin area.
        </h2>
        <p style={{ fontSize: 13 }}>
          Ask an admin to grant you a role if you think this is a mistake.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container" style={{ maxWidth: '100%' }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Admin Dashboard</h1>
          </div>
        </div>
        <Spinner center />
      </div>
    );
  }

  const totalUsers = data?.totalUsers || 0;
  const monthlyEps = data?.monthlyEpisodes || 0;
  const todayEps = data?.todayEpisodes?.length || 0;
  const weekEps = data?.weekEpisodes?.length || 0;
  const allUsers = data?.allUsers || [];
  const users = data?.users || [];

  // Cost calculations
  const monthCost = (monthlyEps * COST_PER_EPISODE).toFixed(2);
  const todayCost = (todayEps * COST_PER_EPISODE).toFixed(2);
  const avgCostPerEp = COST_PER_EPISODE.toFixed(2);
  const projectedMonthly = ((monthlyEps / Math.max(new Date().getDate(), 1)) * 30 * COST_PER_EPISODE).toFixed(2);

  // ── Real Claude usage aggregation (powers the Costs tab) ───────────────
  const usage30dRows = data?.usage30d || [];
  const usageAvailable = data?.usage30d !== null;
  const today30d = aggregateUsage(usage30dRows, 1 * 86400 * 1000);
  const week30d  = aggregateUsage(usage30dRows, 7 * 86400 * 1000);
  const full30d  = aggregateUsage(usage30dRows);
  const recentUsageRows = data?.usageRecent || [];

  const topUsers30d = Object.entries(full30d.byUser)
    .sort((a, b) => b[1].cost - a[1].cost)
    .slice(0, 10)
    .map(([userId, agg]) => {
      const profile = allUsers.find(u => u.user_id === userId);
      return {
        userId,
        label: profile?.full_name || profile?.email || (userId === '(unauthenticated)' ? 'Unauthenticated / server' : userId.slice(0, 8)),
        plan: profile?.plan || (userId === '(unauthenticated)' ? '—' : 'Free'),
        cost: agg.cost,
        calls: agg.calls,
      };
    });

  const modelRows30d = Object.entries(full30d.byModel)
    .sort((a, b) => b[1] - a[1])
    .map(([model, cost]) => ({ model, cost, share: full30d.cost > 0 ? (cost / full30d.cost) * 100 : 0 }));

  const endpointRows30d = Object.entries(full30d.byEndpoint)
    .sort((a, b) => b[1] - a[1])
    .map(([endpoint, cost]) => ({ endpoint, cost, share: full30d.cost > 0 ? (cost / full30d.cost) * 100 : 0 }));

  // Plan breakdown
  const planCounts = { Free: 0, Creator: 0, Pro: 0 };
  allUsers.forEach(u => {
    const plan = u.plan || 'Free';
    if (planCounts[plan] !== undefined) planCounts[plan]++;
    else planCounts.Free++;
  });

  // Niche breakdown
  const nicheCounts = {};
  allUsers.forEach(u => {
    const n = u.niche || 'Not set';
    nicheCounts[n] = (nicheCounts[n] || 0) + 1;
  });
  const topNiches = Object.entries(nicheCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="page-container" style={{ maxWidth: '100%' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {timeGreeting}{adminFirstName ? `, ${adminFirstName}` : ''}. Welcome to the control room.
          </h1>
          <p className="page-subtitle">Here’s everything happening across Epio today.</p>
        </div>
        <span className="admin-internal-badge">Internal</span>
      </div>

      {/* Tabs — visible set depends on the caller's scopes.
          Admins see everything. Managers only see tabs their scopes
          unlock. The Team + Drew's-pick tabs are admin-only because
          they touch role assignments / curation. */}
      <AdminPageTabs activeTab={activeTab} onChange={setActiveTab} />

      {/* ── OVERVIEW ── */}
      {activeTab === 'overview' && (
        <>
          <div className="admin-cost-grid">
            <CostCard label="Total users" value={totalUsers} sub="All time" />
            <CostCard label="Episodes today" value={todayEps} sub="Generated today" trend={todayEps > 0 ? `${todayEps} builds` : 'None yet'} trendDir={todayEps > 0 ? 'up' : 'neutral'} />
            <CostCard label="Episodes this week" value={weekEps} sub="Last 7 days" />
            <CostCard label="Episodes this month" value={monthlyEps} sub={new Date().toLocaleDateString(undefined, { month: 'long' })} />
            <CostCard label="Cost today (real)" value={`$${(today30d.cost || 0).toFixed(4)}`} sub={`${today30d.calls || 0} Claude calls`} />
            <CostCard label="Cost this week (real)" value={`$${(week30d.cost || 0).toFixed(4)}`} sub={`${week30d.calls || 0} Claude calls`} />
            {/* Mini-spec #60: all-time API spend summed across every
                claude_usage row. This is "personal spend on Claude API"
                from Drew's POV since every call routes through his key. */}
            <CostCard
              label="All-time API spend"
              value={`$${(data?.allTimeSpend || 0).toFixed(4)}`}
              sub="Every Claude call ever"
              trend={data?.allTimeSpend > 100 ? 'Time to optimize' : 'Healthy burn'}
              trendDir={data?.allTimeSpend > 100 ? 'down' : 'neutral'}
            />
            <CostCard label="Legacy cost-est (month)" value={`$${monthCost}`} sub={`${monthlyEps} eps × $${avgCostPerEp}`} trend={`~$${projectedMonthly} projected`} trendDir="neutral" />
          </div>

          <div className="admin-two-col">
            {/* Recent episodes */}
            <div className="admin-section-card">
              <div className="admin-section-title">Today's episodes</div>
              {(data?.todayEpisodes || []).length === 0 ? (
                <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No episodes generated today yet</div>
              ) : (
                <div className="admin-activity-list">
                  {(data?.todayEpisodes || []).slice(0, 8).map((ep, i) => (
                    <div key={i} className="admin-activity-row">
                      <div className="admin-activity-time">{timeAgo(ep.created_at)}</div>
                      <div>
                        <div className="admin-activity-event">{ep.topic || 'Untitled'}</div>
                        <div className="admin-activity-user">{ep.format || 'solo'} · ~${COST_PER_EPISODE.toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plan distribution */}
            <div className="admin-section-card">
              <div className="admin-section-title">Plan distribution</div>
              {['Free', 'Creator', 'Pro'].map(plan => {
                const count = planCounts[plan];
                const pct = totalUsers > 0 ? (count / totalUsers) * 100 : 0;
                return (
                  <div key={plan} className="admin-plan-row">
                    <div className="admin-plan-name" style={{ color: PLAN_COLORS[plan] }}>{plan}</div>
                    <div className="admin-plan-bar-wrap">
                      <div className="admin-plan-bar" style={{ width: `${pct}%`, background: PLAN_COLORS[plan] }} />
                    </div>
                    <div className="admin-plan-count">{count}</div>
                  </div>
                );
              })}

              <div className="admin-section-title" style={{ marginTop: 20 }}>Top niches</div>
              {topNiches.map(([niche, count]) => (
                <div key={niche} className="admin-activity-row">
                  <div className="admin-activity-event">{niche}</div>
                  <div className="admin-activity-time" style={{ marginLeft: 'auto' }}>{count} users</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── COSTS — real Claude spend from claude_usage ── */}
      {activeTab === 'costs' && (
        <>
          {!usageAvailable ? (
            <div className="admin-section-card">
              <div className="admin-section-title">Claude usage table not available</div>
              <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55, padding: '8px 0' }}>
                The <code>claude_usage</code> table isn't readable from this session. Apply migration{' '}
                <code>supabase-migrations/017_claude_usage.sql</code> in the Supabase SQL editor,
                then make sure <code>SUPABASE_URL</code> and <code>SUPABASE_SERVICE_ROLE_KEY</code>{' '}
                are set in Vercel → Settings → Environment Variables so the API routes can write to it.
                {data?.usageError && (
                  <div style={{ marginTop: 10, color: 'rgba(255,100,100,0.8)' }}>
                    Error: {data.usageError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="admin-cost-grid">
                <CostCard label="Spend today"    value={`$${today30d.cost.toFixed(4)}`} sub={`${today30d.calls} calls · ${today30d.errors} errors`} />
                <CostCard label="Spend 7d"       value={`$${week30d.cost.toFixed(4)}`}  sub={`${week30d.calls} calls`} />
                <CostCard label="Spend 30d"      value={`$${full30d.cost.toFixed(2)}`}  sub={`${full30d.calls} calls`} />
                <CostCard
                  label="Projected 30d"
                  value={`$${((week30d.cost / 7) * 30).toFixed(2)}`}
                  sub="From last 7d pace"
                  trend={week30d.cost > 0 ? `$${(week30d.cost / Math.max(week30d.calls, 1)).toFixed(4)} / call` : 'No recent calls'}
                  trendDir="neutral"
                />
              </div>

              <div className="admin-two-col">
                {/* Breakdown by model */}
                <div className="admin-section-card">
                  <div className="admin-section-title">Spend by model · 30d</div>
                  {modelRows30d.length === 0 ? (
                    <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No calls in the last 30 days</div>
                  ) : (
                    modelRows30d.map(row => (
                      <div key={row.model} className="admin-plan-row">
                        <div className="admin-plan-name" style={{ color: '#ffb101', minWidth: 180, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }}>{row.model}</div>
                        <div className="admin-plan-bar-wrap">
                          <div className="admin-plan-bar" style={{ width: `${Math.max(row.share, 2)}%`, background: '#ffb101' }} />
                        </div>
                        <div className="admin-plan-count" style={{ minWidth: 80, textAlign: 'right' }}>${row.cost.toFixed(4)}</div>
                      </div>
                    ))
                  )}
                </div>

                {/* Breakdown by endpoint */}
                <div className="admin-section-card">
                  <div className="admin-section-title">Spend by endpoint · 30d</div>
                  {endpointRows30d.length === 0 ? (
                    <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No calls in the last 30 days</div>
                  ) : (
                    endpointRows30d.map(row => (
                      <div key={row.endpoint} className="admin-plan-row">
                        <div className="admin-plan-name" style={{ color: '#6C7FFF', minWidth: 180, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }}>/{row.endpoint}</div>
                        <div className="admin-plan-bar-wrap">
                          <div className="admin-plan-bar" style={{ width: `${Math.max(row.share, 2)}%`, background: '#6C7FFF' }} />
                        </div>
                        <div className="admin-plan-count" style={{ minWidth: 80, textAlign: 'right' }}>${row.cost.toFixed(4)}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="admin-section-card" style={{ marginTop: 16 }}>
                <div className="admin-section-title">Top users by spend · 30d</div>
                {topUsers30d.length === 0 ? (
                  <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No attributed calls yet</div>
                ) : (
                  topUsers30d.map(u => (
                    <div key={u.userId} className="admin-activity-row">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="admin-activity-event" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.label}</div>
                        <div className="admin-activity-user">{u.plan} · {u.calls} calls</div>
                      </div>
                      <div style={{ color: '#ffb101', fontVariantNumeric: 'tabular-nums', fontWeight: 700, marginLeft: 'auto' }}>${u.cost.toFixed(4)}</div>
                    </div>
                  ))
                )}
              </div>

              <div className="admin-section-card" style={{ marginTop: 16 }}>
                <div className="admin-section-title">Recent calls</div>
                {recentUsageRows.length === 0 ? (
                  <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No calls logged yet</div>
                ) : (
                  <div className="admin-activity-list">
                    {recentUsageRows.slice(0, 30).map(r => {
                      const user = allUsers.find(u => u.user_id === r.user_id);
                      const label = user?.full_name || user?.email || (r.user_id ? r.user_id.slice(0, 8) : '—');
                      return (
                        <div key={r.id} className="admin-activity-row" style={{ opacity: r.error ? 0.65 : 1 }}>
                          <div className="admin-activity-time" style={{ minWidth: 80 }}>{timeAgo(r.created_at)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="admin-activity-event" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              /{r.endpoint} · <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, opacity: 0.75 }}>{r.model}</span>
                              {r.error && <span style={{ color: '#ff6969', marginLeft: 6 }}>· error</span>}
                            </div>
                            <div className="admin-activity-user">
                              {label} · {r.input_tokens || 0} in / {r.output_tokens || 0} out
                              {r.duration_ms != null && ` · ${(r.duration_ms / 1000).toFixed(1)}s`}
                            </div>
                          </div>
                          <div style={{ color: r.error ? 'var(--muted)' : '#ffb101', fontVariantNumeric: 'tabular-nums', fontWeight: 600, marginLeft: 'auto' }}>
                            ${Number(r.estimated_cost_usd || 0).toFixed(4)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Keep the legacy episode-count estimate as a sanity check below */}
          <div className="admin-section-card" style={{ marginTop: 16, opacity: 0.85 }}>
            <div className="admin-section-title">Episode-count estimate (legacy)</div>
            <div className="admin-realtime-row">
              <span className="admin-realtime-label">Episodes this month × ${COST_PER_EPISODE.toFixed(2)}</span>
              <span className="admin-realtime-val">${(monthlyEps * COST_PER_EPISODE).toFixed(2)}</span>
            </div>
            <div className="admin-realtime-row">
              <span className="admin-realtime-label">Daily ideas (est. 30% of users)</span>
              <span className="admin-realtime-val">${(totalUsers * COST_PER_IDEA_REFRESH * 0.3).toFixed(2)}</span>
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 8 }}>
              Use the real 30d figure above — this row is a fallback estimate for verification only.
            </div>
          </div>
        </>
      )}

      {/* ── USERS ── */}
      {activeTab === 'users' && (
        <div className="admin-section-card" style={{ padding: 0 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 20px',
            borderBottom: '1px solid rgba(245,237,224,0.06)',
          }}>
            <button
              type="button"
              onClick={() => setShowNewUser(true)}
              style={{
                background: 'rgba(42, 143, 184, 0.18)',
                border: '1px solid rgba(42, 143, 184, 0.35)',
                borderRadius: 6,
                padding: '6px 14px',
                color: '#2A8FB8',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + New user
            </button>
          </div>
          <div className="lib-table-header" style={{ gridTemplateColumns: '1fr 1.2fr 80px 80px 80px 110px 100px 70px 70px', padding: '14px 20px' }}>
            <div>User</div><div>Email</div><div>Plan</div><div>Episodes</div><div>Verified</div><div>Joined</div><div>Credits</div><div>Detail</div><div>Delete</div>
          </div>
          {users.length === 0 ? (
            <div style={{ padding: '20px', color: 'var(--muted)', fontSize: 13 }}>No users found</div>
          ) : (
            users.map(u => (
              <div key={u.user_id} className="lib-episode-row" style={{ gridTemplateColumns: '1fr 1.2fr 80px 80px 80px 110px 100px 70px 70px', padding: '14px 20px' }}>
                <div className="lib-ep-topic">{u.full_name || 'No name'}</div>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>{u.email}</div>
                <div><span style={{ color: PLAN_COLORS[u.plan || 'Free'], fontSize: 12, fontWeight: 600 }}>{u.plan || 'Free'}</span></div>
                <div style={{ color: 'var(--cream)', fontSize: 14 }}>{u.episodes_created || 0}</div>
                <div>
                  <button
                    style={{
                      background: u.verified ? 'rgba(29,155,240,0.15)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${u.verified ? 'rgba(29,155,240,0.3)' : 'rgba(245,237,224,0.1)'}`,
                      borderRadius: 6,
                      padding: '4px 10px',
                      color: u.verified ? '#1d9bf0' : 'rgba(245,237,224,0.4)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    onClick={async () => {
                      try {
                        const { error } = await supabase
                          .from('podcaster_profiles')
                          .update({ verified: !u.verified })
                          .eq('user_id', u.user_id);
                        if (error) throw error;
                        // Refresh data
                        u.verified = !u.verified;
                        setData(prev => ({ ...prev })); // force re-render
                      } catch (err) {
                        alert('Failed to update: ' + (err?.message || 'Unknown error'));
                      }
                    }}
                  >
                    {u.verified ? '✓ Verified' : 'Verify'}
                  </button>
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                  {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                </div>
                <div>
                  <GrantCreditsButton user={u} />
                </div>
                <div>
                  <button
                    style={{
                      background: 'rgba(42, 143, 184, 0.14)',
                      border: '1px solid rgba(42, 143, 184, 0.30)',
                      borderRadius: 6,
                      padding: '4px 10px',
                      color: '#2A8FB8',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                    onClick={() => setOpenUser(u)}
                  >
                    Open
                  </button>
                </div>
                <div>
                  <DeleteUserButton
                    user={u}
                    onDeleted={() => fetchAdminData().then(d => setData(d)).catch(() => {})}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Mini-spec #60: user detail modal — full profile + edit + credits. */}
      {openUser && (
        <UserDetailModal
          user={openUser}
          onClose={() => setOpenUser(null)}
          onSaved={() => {
            // Re-fetch admin data so list reflects edits
            fetchAdminData().then(d => setData(d)).catch(() => {});
          }}
        />
      )}

      {showNewUser && (
        <NewUserModal
          onClose={() => setShowNewUser(false)}
          onCreated={() => {
            setShowNewUser(false);
            fetchAdminData().then(d => setData(d)).catch(() => {});
          }}
        />
      )}

      {/* ── VERIFICATIONS — paid blue check toggle ── */}
      {activeTab === 'verifications' && <VerificationsTab />}

      {/* ── SUPPORT ── */}
      {activeTab === 'support' && <AdminSupportPanel />}

      {/* ── REPORTS — content moderation queue (admin or 'moderation') ── */}
      {activeTab === 'reports' && <AdminReportsPanel />}

      {/* ── TEAM (admin only) — grant manager / admin roles ── */}
      {activeTab === 'team' && <AdminTeamPanel />}

      {/* ── DREW'S PICK (mini-spec #8) ── */}
      {activeTab === "drew's pick" && (
        <div className="admin-section-card" style={{ padding: 0 }}>
          <DrewsPickTab />
        </div>
      )}

      {/* ── FEATURED — curate the home "Staff Picks" row ── */}
      {activeTab === 'featured' && (
        <div className="admin-section-card">
          <FeaturedTab />
        </div>
      )}

      {/* ── SPONSORS — manage the sponsor catalog ── */}
      {activeTab === 'sponsors' && (
        <div className="admin-section-card">
          <SponsorsTab />
        </div>
      )}

      {/* ── BRANDS — vet sponsor accounts before their deals reach hosts ── */}
      {activeTab === 'brands' && (
        <div className="admin-section-card">
          <BrandsTab />
        </div>
      )}

      {/* ── AUDIT — read-only log of admin actions ── */}
      {activeTab === 'audit' && (
        <div className="admin-section-card">
          <AuditTab />
        </div>
      )}

      {/* ── ACTIVITY ── */}
      {activeTab === 'activity' && (
        <div className="admin-section-card">
          <div className="admin-section-title">Recent episode builds</div>
          {(data?.weekEpisodes || []).length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '12px 0' }}>No recent activity</div>
          ) : (
            <div className="admin-activity-list">
              {(data?.weekEpisodes || []).slice(0, 20).map((ep, i) => (
                <div key={i} className="admin-activity-row">
                  <div className="admin-activity-time" style={{ minWidth: 80 }}>{timeAgo(ep.created_at)}</div>
                  <div>
                    <div className="admin-activity-event">{ep.topic || 'Untitled'}</div>
                    <div className="admin-activity-user">{ep.format || 'solo'} · ~${COST_PER_EPISODE.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Mini-spec #59 — per-user "Grant credits" button. Prompts for an
// amount + a short note, calls the admin_grant_credits RPC, surfaces
// Inline "Delete" button for the admin users list. Hard-deletes the auth
// user via the service-role endpoint — works for Google / Apple / email
// alike. Cascading FKs on public.* clean up the rest. Confirms twice
// (window.confirm + a second prompt for the email) so a stray click
// can't nuke an account.
function DeleteUserButton({ user, onDeleted }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    if (busy) return;
    const email = user?.email || '(unknown)';
    if (!window.confirm(`Delete ${email}? This removes the auth user and every row keyed to them. There is no undo.`)) return;
    const typed = window.prompt(`Type the email to confirm:\n${email}`);
    if (!typed || typed.trim().toLowerCase() !== String(email).toLowerCase()) {
      toast?.info?.('Cancelled — email didn\'t match.');
      return;
    }
    setBusy(true);
    try {
      await adminDeleteUser({ userId: user.user_id });
      toast?.success?.(`Deleted ${email}`);
      onDeleted?.();
    } catch (e) {
      toast?.error?.(e?.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      style={{
        background: 'rgba(214, 24, 73, 0.14)',
        border: '1px solid rgba(214, 24, 73, 0.30)',
        borderRadius: 6,
        padding: '4px 10px',
        color: '#d61849',
        fontSize: 11,
        fontWeight: 600,
        cursor: busy ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? '...' : 'Delete'}
    </button>
  );
}

// success/failure inline. Caller must be is_admin = true on
// podcaster_profiles (RPC enforces this server-side).
function GrantCreditsButton({ user }) {
  const toast = useToast();
  const [busy, setBusy]         = useState(false);
  // Two-step flow: amount first, then optional note. We keep them as
  // sequential modals so the existing logic (validate amount, fail
  // fast) doesn't change.
  const [amountOpen, setAmountOpen] = useState(false);
  const [noteOpen,   setNoteOpen]   = useState(false);
  const [amount,     setAmount]     = useState(0);

  async function performGrant(note) {
    setBusy(true);
    try {
      const result = await grantCredits(user.user_id, amount, note || 'manual grant');
      toast?.success?.(`Granted ${amount}. New topup: ${result?.topup_balance}, total: ${result?.total}`);
    } catch (err) {
      toast?.error?.(`Grant failed: ${err?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
      setNoteOpen(false);
    }
  }

  return (
    <>
      <button
        type="button"
        style={{
          background: 'rgba(235, 155, 50, 0.14)',
          border: '1px solid rgba(235, 155, 50, 0.30)',
          borderRadius: 6,
          padding: '4px 10px',
          color: '#EB9B32',
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
        onClick={() => setAmountOpen(true)}
        disabled={busy}
      >
        {busy ? '…' : '+ Credits'}
      </button>

      <AdminPromptModal
        open={amountOpen}
        title={`Grant credits to ${user.full_name || user.email}?`}
        body="Amount (1–100000)"
        defaultValue="500"
        placeholder="500"
        confirmLabel="Next"
        onCancel={() => setAmountOpen(false)}
        onConfirm={(raw) => {
          const n = parseInt(raw, 10);
          if (!Number.isFinite(n) || n <= 0) { toast?.error?.('Enter a positive integer.'); return; }
          setAmount(n);
          setAmountOpen(false);
          setNoteOpen(true);
        }}
      />

      <AdminPromptModal
        open={noteOpen}
        title="Note for the ledger"
        body="Optional — defaults to 'manual grant'."
        defaultValue="manual grant"
        confirmLabel={`Grant ${amount}`}
        busy={busy}
        onCancel={() => { if (!busy) setNoteOpen(false); }}
        onConfirm={(note) => performGrant(note)}
      />
    </>
  );
}

// Mini-spec #60 — full user-detail modal. Pulls extended fields (we
// already have the basics from the table query), edits via the
// admin_update_user RPC. Credit adjustments use admin_set_credits +
// admin_grant_credits depending on whether the admin is overwriting
// or topping up.
function UserDetailModal({ user, onClose, onSaved }) {
  const toast = useToast();
  const [creds, setCreds]   = useState(null);
  const [extra, setExtra]   = useState(null);
  const [usage, setUsage]   = useState(null);
  const [shows, setShows]   = useState([]);
  const [busy,  setBusy]    = useState(false);
  // Credit-adjust prompt modal (replaces window.prompt). `mode` is 'set'
  // | 'add' to match the original two-button UI.
  const [creditMode, setCreditMode] = useState(null); // null | 'set' | 'add'
  const [form,  setForm]    = useState({
    full_name:           user.full_name || '',
    plan:                user.plan || 'Free',
    niche:               user.niche || '',
    professional_title:  '',
    bio:                 '',
    marketplace_visible: true,
    is_admin:            false,
    unlimited_credits:   false,
    verified:            !!user.verified,
  });

  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    (async () => {
      try {
        const [profileRes, credsRes, usageRes, showsRes] = await Promise.all([
          supabase.from('podcaster_profiles')
            .select('*')
            .eq('user_id', user.user_id)
            .maybeSingle(),
          supabase.from('user_credits')
            .select('balance, topup_balance, monthly_allotment, last_refilled_at')
            .eq('user_id', user.user_id)
            .maybeSingle(),
          supabase.from('claude_usage')
            .select('estimated_cost_usd, created_at, endpoint, model')
            .eq('user_id', user.user_id)
            .order('created_at', { ascending: false })
            .limit(500),
          supabase.from('shows')
            .select('id, name, episode_count, is_publicly_listed, created_at')
            .eq('user_id', user.user_id),
        ]);
        if (!alive) return;
        if (profileRes.data) {
          setExtra(profileRes.data);
          setForm(f => ({
            ...f,
            full_name:           profileRes.data.full_name || '',
            plan:                profileRes.data.plan || 'Free',
            niche:               profileRes.data.niche || '',
            professional_title:  profileRes.data.professional_title || '',
            bio:                 profileRes.data.bio || '',
            marketplace_visible: profileRes.data.marketplace_visible !== false,
            is_admin:            !!profileRes.data.is_admin,
            unlimited_credits:   !!profileRes.data.unlimited_credits,
            verified:            !!profileRes.data.verified,
          }));
        }
        if (credsRes.data) setCreds(credsRes.data);
        if (usageRes.data) {
          const total = usageRes.data.reduce((acc, r) => acc + (Number(r.estimated_cost_usd) || 0), 0);
          setUsage({ total, calls: usageRes.data.length, recent: usageRes.data.slice(0, 5) });
        }
        if (showsRes.data) setShows(showsRes.data);
      } catch (err) {
        console.error('UserDetailModal load:', err);
      }
    })();
    return () => { alive = false; };
  }, [user.user_id]);

  async function saveProfile() {
    setBusy(true);
    try {
      await updateUserAdmin(user.user_id, {
        full_name:           form.full_name || null,
        plan:                form.plan || 'Free',
        niche:               form.niche || null,
        professional_title:  form.professional_title || null,
        bio:                 form.bio || null,
        marketplace_visible: !!form.marketplace_visible,
        is_admin:            !!form.is_admin,
        unlimited_credits:   !!form.unlimited_credits,
        verified:            !!form.verified,
      });
      // Mini-spec #61: tell AuthContext to re-fetch the profile so
      // plan / unlimited / admin flag changes take effect on the
      // signed-in user without requiring sign-out/sign-in.
      try { window.dispatchEvent(new Event('profile:refresh')); } catch {}
      try { window.dispatchEvent(new Event('credits:changed')); } catch {}
      toast?.success?.('Profile updated.');
      onSaved?.();
    } catch (err) {
      toast?.error?.('Save failed: ' + (err?.message || err));
    } finally { setBusy(false); }
  }

  function adjustCredits(mode) {
    setCreditMode(mode);
  }

  async function performAdjustCredits(raw) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < 0) { toast?.error?.('Enter a non-negative integer.'); return; }
    setBusy(true);
    try {
      const result = creditMode === 'set'
        ? await setCreditsExact(user.user_id, n, 'admin set via detail modal')
        : await grantCredits(user.user_id, n, 'admin grant via detail modal');
      toast?.success?.(`OK. New topup: ${result?.topup_balance ?? n}`);
      // refresh creds
      const { data } = await supabase.from('user_credits').select('balance, topup_balance, monthly_allotment, last_refilled_at').eq('user_id', user.user_id).maybeSingle();
      if (data) setCreds(data);
      setCreditMode(null);
    } catch (err) {
      toast?.error?.('Failed: ' + (err?.message || err));
    } finally { setBusy(false); }
  }

  return (
    <div className="udm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="udm-modal" role="dialog" aria-modal="true">
        <button className="udm-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="udm-title">{form.full_name || user.email || 'User'}</h2>
        <div className="udm-sub">{user.email}</div>

        <div className="udm-grid">
          <div className="udm-stat">
            <div className="udm-stat-label">Plan</div>
            <div className="udm-stat-value">{form.plan}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Episodes</div>
            <div className="udm-stat-value">{user.episodes_created || 0}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Credits (topup)</div>
            <div className="udm-stat-value">{creds?.topup_balance ?? '—'}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Credits (monthly)</div>
            <div className="udm-stat-value">{creds?.balance ?? '—'}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">API spend</div>
            <div className="udm-stat-value">${(usage?.total || 0).toFixed(4)}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Claude calls</div>
            <div className="udm-stat-value">{usage?.calls || 0}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Shows</div>
            <div className="udm-stat-value">{shows.length}</div>
          </div>
          <div className="udm-stat">
            <div className="udm-stat-label">Joined</div>
            <div className="udm-stat-value">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</div>
          </div>
        </div>

        <div className="udm-section-head">Credits</div>
        <div className="udm-row">
          <button className="udm-btn udm-btn--amber" onClick={() => adjustCredits('grant')} disabled={busy}>+ Grant credits</button>
          <button className="udm-btn" onClick={() => adjustCredits('set')} disabled={busy}>Set exact (incl. zero)</button>
        </div>

        <div className="udm-section-head">Auth credentials</div>
        <AuthCredentialsEditor user={user} onChanged={onSaved} />

        <div className="udm-section-head">Edit profile</div>
        <div className="udm-form">
          <label className="udm-field">
            <span>Display name</span>
            <input type="text" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
          </label>
          <label className="udm-field">
            <span>Plan</span>
            <select value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
              <option>Free</option>
              <option>Creator</option>
              <option>Pro</option>
              <option>Studio</option>
            </select>
          </label>
          <label className="udm-field">
            <span>Niche</span>
            <input type="text" value={form.niche} onChange={e => setForm(f => ({ ...f, niche: e.target.value }))} />
          </label>
          <label className="udm-field">
            <span>Professional title</span>
            <input type="text" value={form.professional_title} onChange={e => setForm(f => ({ ...f, professional_title: e.target.value }))} />
          </label>
          <label className="udm-field udm-field--full">
            <span>Bio</span>
            <textarea rows={3} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
          </label>
          <label className="udm-toggle"><input type="checkbox" checked={form.marketplace_visible} onChange={e => setForm(f => ({ ...f, marketplace_visible: e.target.checked }))} /> Public profile (Explore-visible)</label>
          <label className="udm-toggle"><input type="checkbox" checked={form.verified} onChange={e => setForm(f => ({ ...f, verified: e.target.checked }))} /> Verified</label>
          <label className="udm-toggle"><input type="checkbox" checked={form.is_admin} onChange={e => setForm(f => ({ ...f, is_admin: e.target.checked }))} /> Admin</label>
          <label className="udm-toggle"><input type="checkbox" checked={form.unlimited_credits} onChange={e => setForm(f => ({ ...f, unlimited_credits: e.target.checked }))} /> Unlimited credits</label>
        </div>

        {usage?.recent?.length > 0 && (
          <>
            <div className="udm-section-head">Recent Claude calls</div>
            <ul className="udm-recent">
              {usage.recent.map((r, i) => (
                <li key={i}>
                  <span className="udm-recent-when">{new Date(r.created_at).toLocaleString()}</span>
                  <span className="udm-recent-ep">{r.endpoint}</span>
                  <span className="udm-recent-model">{r.model}</span>
                  <span className="udm-recent-cost">${(Number(r.estimated_cost_usd) || 0).toFixed(4)}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="udm-actions">
          <button className="udm-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="udm-btn udm-btn--primary" onClick={saveProfile} disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <AdminPromptModal
        open={!!creditMode}
        title={creditMode === 'set' ? 'Set topup credits' : 'Add credits'}
        body={creditMode === 'set'
          ? `Current topup: ${creds?.topup_balance ?? 0}. Enter the new total.`
          : `Current topup: ${creds?.topup_balance ?? 0}. Enter how many to ADD.`}
        defaultValue={creditMode === 'set' ? String(creds?.topup_balance ?? 0) : '500'}
        confirmLabel={creditMode === 'set' ? 'Set credits' : 'Add credits'}
        busy={busy}
        onCancel={() => { if (!busy) setCreditMode(null); }}
        onConfirm={performAdjustCredits}
      />
    </div>
  );
}

// Edit a user's auth email and/or password. Lives inside UserDetailModal.
// Both fields are independent — leaving one blank means "don't change it".
// Routes through /api/admin-users which re-checks is_admin server-side.
function AuthCredentialsEditor({ user, onChanged }) {
  const [email, setEmail]       = useState(user.email || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState(null);
  // In-app confirm replaces window.confirm so the destructive password
  // reset stays consistent with the rest of the app.
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function performSave() {
    const trimmed = email.trim().toLowerCase();
    const emailChanged = trimmed && trimmed !== (user.email || '').toLowerCase();
    const pwChanged = password.length > 0;
    setBusy(true);
    setMsg(null);
    try {
      await adminUpdateUserAuth({
        userId:   user.user_id,
        email:    emailChanged ? trimmed : undefined,
        password: pwChanged    ? password : undefined,
      });
      setPassword('');
      setMsg({ kind: 'ok', text: 'Saved.' });
      onChanged?.();
    } catch (err) {
      setMsg({ kind: 'err', text: err?.message || 'Failed' });
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  function save() {
    const trimmed = email.trim().toLowerCase();
    const emailChanged = trimmed && trimmed !== (user.email || '').toLowerCase();
    const pwChanged = password.length > 0;
    if (!emailChanged && !pwChanged) {
      setMsg({ kind: 'err', text: 'Change the email or enter a new password first.' });
      return;
    }
    if (pwChanged && password.length < 6) {
      setMsg({ kind: 'err', text: 'Password must be at least 6 characters.' });
      return;
    }
    if (pwChanged) { setConfirmOpen(true); return; }
    performSave();
  }

  return (
    <div className="udm-form">
      <label className="udm-field">
        <span>Email</span>
        <input
          type="email"
          value={email}
          autoComplete="off"
          onChange={e => setEmail(e.target.value)}
          placeholder="user@example.com"
        />
      </label>
      <label className="udm-field">
        <span>New password</span>
        <input
          type="text"
          value={password}
          autoComplete="new-password"
          onChange={e => setPassword(e.target.value)}
          placeholder="Leave blank to keep current"
        />
      </label>
      <div className="udm-field udm-field--full" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          type="button"
          className="udm-btn udm-btn--primary"
          onClick={save}
          disabled={busy}
        >
          {busy ? 'Saving…' : 'Save credentials'}
        </button>
        {msg && (
          <span style={{
            fontSize: 12,
            color: msg.kind === 'ok' ? '#4ade80' : '#FF8888',
          }}>{msg.text}</span>
        )}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={`Reset password for ${user.email}?`}
        body="They will be signed out of all existing sessions and must use the new password to sign in."
        confirmLabel="Reset password"
        tone="danger"
        busy={busy}
        onCancel={() => { if (!busy) setConfirmOpen(false); }}
        onConfirm={performSave}
      />
    </div>
  );
}

// Create a brand-new auth user (email + password). Triggers the same DB
// trigger that runs on normal signup, so a podcaster_profiles row is
// auto-created. Optional display name is mirrored into the profile.
function NewUserModal({ onClose, onCreated }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);

  async function submit() {
    setErr(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/.+@.+\..+/.test(trimmed)) {
      setErr('Valid email required'); return;
    }
    if (password.length < 6) {
      setErr('Password must be at least 6 characters'); return;
    }
    setBusy(true);
    try {
      await adminCreateUser({ email: trimmed, password, fullName: fullName.trim() || null });
      onCreated?.();
    } catch (e) {
      setErr(e?.message || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="udm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="udm-modal" role="dialog" aria-modal="true" style={{ maxWidth: 480 }}>
        <button className="udm-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="udm-title">New user</h2>
        <div className="udm-sub">Create an account directly. Email is auto-confirmed.</div>

        <div className="udm-form" style={{ marginTop: 18 }}>
          <label className="udm-field udm-field--full">
            <span>Email</span>
            <input
              type="email"
              value={email}
              autoComplete="off"
              onChange={e => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </label>
          <label className="udm-field udm-field--full">
            <span>Password</span>
            <input
              type="text"
              value={password}
              autoComplete="new-password"
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
          </label>
          <label className="udm-field udm-field--full">
            <span>Display name (optional)</span>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="Jane Doe"
            />
          </label>
        </div>

        {err && (
          <div style={{ color: '#FF8888', fontSize: 12, marginTop: 8 }}>{err}</div>
        )}

        <div className="udm-actions">
          <button className="udm-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="udm-btn udm-btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Verifications tab ───────────────────────────────────────────────────
// Dedicated surface for granting / revoking the paid blue check. Paid
// verification will roll out to Pro+ tiers later; for now this is the
// manual toggle admins use to verify specific accounts (currently just
// the two admin users).
function VerificationsTab() {
  const toast = useToast();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState('');
  const [busyId, setBusyId]   = useState(null);

  const load = async () => {
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('podcaster_profiles')
      .select('user_id, email, full_name, username, avatar_url, verified, created_at')
      .order('verified', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(500);
    if (error) {
      toast?.error?.('Could not load users.');
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function toggle(row) {
    if (busyId) return;
    setBusyId(row.user_id);
    try {
      const next = !row.verified;
      const { error } = await supabase
        .from('podcaster_profiles')
        .update({ verified: next })
        .eq('user_id', row.user_id);
      if (error) throw error;
      setRows(prev => prev.map(r => r.user_id === row.user_id ? { ...r, verified: next } : r));
      toast?.success?.(next ? 'Verified.' : 'Unverified.');
    } catch (err) {
      toast?.error?.(err?.message || 'Update failed.');
    } finally {
      setBusyId(null);
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r =>
        (r.email || '').toLowerCase().includes(q)
        || (r.full_name || '').toLowerCase().includes(q)
        || (r.username || '').toLowerCase().includes(q)
      )
    : rows;
  const verifiedCount = rows.filter(r => r.verified).length;

  return (
    <div className="admin-section-card" style={{ padding: 0 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(245,237,224,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--cream)' }}>
              Verified accounts <span style={{ color: 'rgba(245,237,224,0.4)', fontWeight: 500 }}>· {verifiedCount}</span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
              Toggle the paid blue check per user. Future: auto-granted on Pro+ tiers.
            </div>
          </div>
          <input
            type="text"
            placeholder="Search by name, @handle, or email"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{
              flex: '1 1 240px',
              maxWidth: 360,
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid rgba(245,237,224,0.12)',
              background: 'rgba(245,237,224,0.04)',
              color: 'var(--cream)',
              fontFamily: 'inherit',
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>
      </div>

      {loading ? (
        <Spinner center />
      ) : filtered.length === 0 ? (
        <div style={{ padding: 20, color: 'var(--muted)', fontSize: 13 }}>
          {q ? `No users match "${query}".` : 'No users yet.'}
        </div>
      ) : (
        <div>
          {filtered.map(r => {
            const name = r.full_name || r.username || r.email?.split('@')[0] || 'Unknown';
            const initials = (name[0] || '?').toUpperCase();
            const busy = busyId === r.user_id;
            return (
              <div
                key={r.user_id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '42px 1.4fr 1fr 130px',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 20px',
                  borderTop: '1px solid rgba(245,237,224,0.04)',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 999,
                  background: 'linear-gradient(135deg, #53167A, #c6364a)',
                  color: '#fff', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 600,
                }}>
                  {r.avatar_url
                    ? <img src={storageImage(r.avatar_url, { width: 96 })} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: 'var(--cream)', fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </div>
                  <div style={{ color: 'var(--muted)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.username ? `@${r.username}` : '—'}
                  </div>
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.email || '—'}
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: busy ? 'wait' : 'pointer', justifyContent: 'flex-end' }}>
                  <input
                    type="checkbox"
                    checked={!!r.verified}
                    onChange={() => toggle(r)}
                    disabled={busy}
                    style={{ width: 16, height: 16, accentColor: '#1d9bf0' }}
                  />
                  <span style={{
                    fontSize: 12, fontWeight: 600,
                    color: r.verified ? '#1d9bf0' : 'rgba(245,237,224,0.55)',
                  }}>
                    {r.verified ? 'Verified' : 'Not verified'}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
